import React, { useEffect, useState } from "react";
import { Hash, Pin, Trash2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SocialEmbed } from "../components/SocialEmbed";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";

export default function SocialPage() {
  const { user } = useAuth();
  const copy = usePageContent("social");
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState("");

  const handleDelete = async (postId) => {
    if (!window.confirm("Remove this social post from the wall?")) return;
    try {
      await api.delete(`/social/${postId}`);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (_) {
      alert("Failed to delete post.");
    }
  };

  useEffect(() => {
    let alive = true;
    api
      .get("/social")
      .then((r) => alive && setPosts(r.data))
      .catch(() => alive && setError("Couldn't load the social wall right now."));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]"
      data-testid="page-social"
    >
      <Header />
      <main className="mx-auto max-w-7xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10 sm:pb-20">
        {copy.hide_header ? null : (
          <div className="border-b border-black/10 pb-6">
            <div className="flex items-center gap-2 text-brand-accent" style={kickerStyle(copy)}>
              <Hash size={16} strokeWidth={2.5} />
              <span className="label-kicker">{copy.kicker}</span>
            </div>
            <h1
              className="brand-headline mt-3 whitespace-pre-line font-display text-4xl font-black tracking-tight sm:text-5xl"
              style={titleStyle(copy)}
            >
              {copy.title}
            </h1>
            {copy.intro ? (
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-[var(--brand-body)]">
                {copy.intro}
              </p>
            ) : null}
          </div>
        )}

        {posts === null ? (
          <div
            className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="social-loading"
          >
            {["s1","s2","s3","s4","s5","s6"].map((id) => (
              <div
                key={id}
                className="h-72 animate-pulse rounded-md border border-black/10 bg-white"
              />
            ))}
          </div>
        ) : error ? (
          <p
            className="mt-10 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="social-error"
          >
            {error}
          </p>
        ) : posts.length === 0 ? (
          <div
            className="mt-12 rounded-md border border-dashed border-black/15 bg-white p-12 text-center"
            data-testid="social-empty"
          >
            <p className="font-display text-lg font-bold">
              The wall's quiet — for now.
            </p>
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              The first #PUSB post that catches the editor's eye will appear
              here. Want to flag one? Use the{" "}
              <a
                href="/contact"
                className="underline decoration-[#6CABDD] underline-offset-4"
              >
                contact form
              </a>
              .
            </p>
          </div>
        ) : (
          <div
            className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="social-grid"
          >
            {posts.map((p) => (
              <div key={p.id} className="relative flex flex-col h-[520px]" data-testid="social-card">
                {p.pinned ? (
                  <span
                    className="absolute -top-2 left-3 z-10 inline-flex items-center gap-1 rounded-sm bg-[#6CABDD] px-2 py-1 label-kicker text-white"
                    data-testid="social-pinned"
                  >
                    <Pin size={10} fill="currentColor" /> Pinned
                  </span>
                ) : null}
                {user ? (
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="absolute top-2 right-3 z-10 inline-flex items-center gap-1.5 rounded-sm bg-red-600 hover:bg-red-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white transition shadow-sm cursor-pointer"
                    data-testid={`social-delete-${p.id}`}
                    title="Delete post"
                  >
                    <Trash2 size={10} /> Delete
                  </button>
                ) : null}
                <SocialEmbed
                  platform={p.platform}
                  url={p.url}
                  caption={p.caption}
                  previewImage={p.preview_image}
                />
              </div>
            ))}
          </div>
        )}

        <div
          className="mt-10 border-t border-black/10 pt-5 text-xs leading-relaxed text-[var(--brand-body)]"
          data-testid="social-editorial-notice"
        >
          <p>
            <strong className="text-[var(--brand-ink)]">Editorial fair use.</strong>{" "}
            Posts on this wall are hosted on their original platform — LAST
            links to public content for editorial / fan-community purposes
            only. Platform names and logos are shown to identify the source,
            not to claim partnership or endorsement. LAST is not affiliated
            with Meta, X, TikTok, ByteDance, or Bluesky.
            {" "}
            If you're the author of a post and want it removed, email{" "}
            <a
              href="mailto:admin@letsallsingtogether.com?subject=Social%20post%20removal%20%E2%80%94%20LAST"
              className="underline decoration-[#6CABDD] underline-offset-4 hover:text-[var(--brand-ink)]"
            >
              admin@letsallsingtogether.com
            </a>
            {" "}— we take it down within 48 hours.
          </p>
          <p className="mt-3">
            Embeds load directly from each network. By viewing them, your
            browser may share its IP with that network — see our{" "}
            <a
              href="/page/privacy"
              className="underline decoration-[#6CABDD] underline-offset-4"
            >
              privacy notice
            </a>
            .
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
